export class HttpError extends Error {
  constructor(public status: number, public body: string, message?: string) {
    super(message ?? `HTTP ${status}`);
  }
}

export interface ApiOptions {
  signal?: AbortSignal;
  body?: unknown;
}

async function request<T>(method: string, path: string, opts: ApiOptions = {}): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    signal: opts.signal,
    headers: opts.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  };
  const resp = await fetch(path, init);
  if (resp.status === 401) {
    // Cookie middleware returns 401 (not 302) for /api so the SPA can redirect.
    window.location.href = `/bff/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    throw new HttpError(401, '', 'Unauthorized — redirecting to sign-in');
  }
  if (resp.status === 403 && resp.headers.get('X-Account-Suspended') === 'true') {
    // Suspended accounts are authenticated but locked out — bounce the whole
    // SPA to the dedicated /suspended page (full nav, like the 401 path).
    window.location.href = '/suspended';
    throw new HttpError(403, '', 'Account suspended — redirecting');
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new HttpError(resp.status, text);
  }
  if (resp.status === 204) return undefined as T;
  const ct = resp.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await resp.json()) as T;
  return (await resp.text()) as unknown as T;
}

export const api = {
  get: <T,>(path: string, opts?: ApiOptions) => request<T>('GET', path, opts),
  post: <T,>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('POST', path, { ...opts, body }),
  put: <T,>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('PUT', path, { ...opts, body }),
  patch: <T,>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('PATCH', path, { ...opts, body }),
  delete: <T,>(path: string, opts?: ApiOptions) => request<T>('DELETE', path, opts),
};
